import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import type { Circle } from '@/lib/types'

export function useCircles() {
  return useQuery({
    queryKey: ['circles'],
    queryFn: () => api<{ items: Circle[]; total: number }>('/api/circles').then((r) => r.items),
  })
}

export function useCreateCircle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; color?: string; icon?: string }) =>
      api<Circle>('/api/circles', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['circles'] })
      toast.success(i18n.t('toast.circleCreated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useUpdateCircle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string
      name?: string
      color?: string
      icon?: string
      sort_order?: number
    }) => api<Circle>(`/api/circles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['circles'] })
      toast.success(i18n.t('toast.circleUpdated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useReorderCircles() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) =>
      api<{ ok: boolean }>('/api/circles/reorder', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['circles'] })
    },
  })
}

export function useDeleteCircle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/circles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['circles'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      toast.success(i18n.t('toast.circleDeleted'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}
